"use client"

import React, { useEffect, useLayoutEffect, useMemo, useState } from "react"
import { useUbPDPState } from "@/store/ubPDPState"
import { ProductPriceContainerCtnrROV1, ProductPriceContainerRO } from "@ub/pricing-client"
import { MinusIcon, PlusIcon } from "lucide-react"
import { useTranslations } from "next-intl"

import { ReducedProductData } from "@/lib/storefront/product/product"
import { cn } from "@/components/ui/helpers/utils"
import AddToBasketButtonClient from "@/components/product/AddToBasketButtonClient"

export interface AddToBaskedWithQuantityInputProps {
  isoLocale: string
  isB2BCustomer: boolean
  productSku: string
  article?: ReducedProductData
  prices: ProductPriceContainerRO | undefined
  minOrderQuantity?: number
  orderQuantitySteps?: number
  compact?: boolean
  localState: boolean
  searchTerm?: string
  trackingList?: string
  position?: number
  campaignId?: string
  simulateProductClick: boolean
}

export function AddToBaskedWithQuantityInputClient({
  isoLocale,
  isB2BCustomer,
  article,
  prices,
  minOrderQuantity,
  orderQuantitySteps,
  productSku,
  compact,
  localState,
  searchTerm,
  trackingList,
  position,
  campaignId,
  simulateProductClick,
}: AddToBaskedWithQuantityInputProps) {
  const t = useTranslations("storefront")
  const minimumOrderQuantity = minOrderQuantity ?? 1
  const quantitySteps = orderQuantitySteps ?? 1

  const { buyboxQuantity, setBuyboxQuantity } = useUbPDPState()

  const initialQuantity = buyboxQuantity ?? Math.min(minimumOrderQuantity, quantitySteps)

  const [localQuantity, setLocalQuantity] = useState<number>(initialQuantity)
  const [visibleQuantityField, setVisibleQuantityField] = useState<string>(initialQuantity.toString())

  const setQuantity = localState ? setLocalQuantity : setBuyboxQuantity

  const quantity = useMemo(() => {
    return localState ? localQuantity : buyboxQuantity
  }, [localState, localQuantity, buyboxQuantity])

  const priceContainer: ProductPriceContainerCtnrROV1 | undefined = prices
    ? {
        data: [prices],
      }
    : undefined

  function decreaseQuantity() {
    updateAndShowQuantity(quantity - (quantitySteps ?? 1))
  }

  function increaseQuantity() {
    updateAndShowQuantity(quantity + (quantitySteps ?? 1))
  }

  function handleQuantityBlur(event: React.FocusEvent<HTMLInputElement>) {
    // reflect interally calculated quantity in visibleQuantityField
    setVisibleQuantityField(quantity.toString())
  }

  function handleQuantityChange(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.target.value

    setVisibleQuantityField(value)

    if (/^\d*$/.test(value)) {
      if (value === "") {
        updateQuantity(1)
        return
      }

      const intValue = parseInt(value)
      updateQuantity(intValue)
    }
  }

  function updateQuantity(wantedQuantity: number, showVisible = false) {
    if (!(wantedQuantity > 0)) {
      return
    }

    const newAddableQuantity = wantedQuantity - (wantedQuantity % quantitySteps)
    const newQuantity = Math.max(minimumOrderQuantity, newAddableQuantity)
    setQuantity(newQuantity)

    if (showVisible) {
      setVisibleQuantityField(newQuantity.toString())
    }
  }

  function updateAndShowQuantity(newQuantity: number) {
    updateQuantity(newQuantity, true)
  }

  function stopNestedProductCardClick(event: React.SyntheticEvent) {
    event.preventDefault()
    event.stopPropagation()
  }

  useLayoutEffect(() => {
    updateAndShowQuantity(initialQuantity)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!localState) {
      updateAndShowQuantity(buyboxQuantity)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyboxQuantity])

  return (
    <>
      <div
        data-skip-product-click-tracking
        className={cn(
          "flex shrink-0 flex-row items-center justify-center rounded-lg bg-gray-100 p-0",
          { "w-auto min-w-32 px-2 lg:w-auto": compact },
          { "w-auto lg:w-2/6": !compact }
        )}
      >
        <button
          type="button"
          className={cn("w-auto rounded-full py-1 pr-2 pl-4")}
          onClick={(event) => {
            stopNestedProductCardClick(event)
            decreaseQuantity()
          }}
          aria-label={t("product.quantity_decrease")}
          disabled={quantity <= minimumOrderQuantity}
        >
          <MinusIcon
            className={cn("size-5", {
              "text-muted-foreground opacity-20": quantity <= minimumOrderQuantity,
            })}
          />
        </button>
        <input
          type="text"
          aria-label={t("product.quantity")}
          className={cn(
            `
              h-9 border-transparent bg-transparent p-0 py-1 text-center text-lg font-bold
              focus:rounded-md focus:border-2 focus:border-blue-400 focus:ring-transparent focus:outline-hidden
            `,
            { "w-12": compact },
            { "w-16 grow": !compact }
          )}
          value={visibleQuantityField}
          onClick={stopNestedProductCardClick}
          onChange={handleQuantityChange}
          onBlur={handleQuantityBlur}
        />
        <button
          type="button"
          className={cn("w-auto rounded-full py-1 pr-4 pl-2")}
          onClick={(event) => {
            stopNestedProductCardClick(event)
            increaseQuantity()
          }}
          aria-label={"product.quantity_increase"}
        >
          <PlusIcon className={"size-5"} />
        </button>
      </div>

      {compact ? (
        <div className={"w-full"}>
          <AddToBasketButtonClient
            isoLocale={isoLocale}
            isB2BCustomer={isB2BCustomer}
            className={"w-full min-w-16"}
            articleIdToAdd={productSku}
            productData={article}
            prices={priceContainer}
            quantity={quantity}
            searchTerm={searchTerm ?? ""}
            trackingList={trackingList}
            position={position}
            campaignId={campaignId}
            simulateProductClick={simulateProductClick}
          />
        </div>
      ) : (
        <>
          <div className={"ml-auto w-full lg:hidden"}>
            <AddToBasketButtonClient
              isoLocale={isoLocale}
              isB2BCustomer={isB2BCustomer}
              articleIdToAdd={productSku}
              productData={article}
              prices={priceContainer}
              quantity={quantity}
              searchTerm={searchTerm}
              trackingList={trackingList}
              position={position}
              campaignId={campaignId}
              simulateProductClick={simulateProductClick}
            />
          </div>
          <div className={"ml-auto hidden w-full lg:block"}>
            <AddToBasketButtonClient
              isoLocale={isoLocale}
              isB2BCustomer={isB2BCustomer}
              className={"w-full gap-2"}
              label={t("product.add_to_basket")}
              articleIdToAdd={productSku}
              productData={article}
              prices={priceContainer}
              quantity={quantity}
              searchTerm={searchTerm}
              trackingList={trackingList}
              position={position}
              campaignId={campaignId}
              simulateProductClick={simulateProductClick}
            />
          </div>
        </>
      )}
    </>
  )
}
